import { useTranslation } from 'react-i18next';
import './NetworkSwitch.scss';

export interface NetworkSwitchProps {
  isOffline: boolean;
  isLoading: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

/**
 * NetworkSwitch Component
 * @description Toggle switch for switching between Online and Offline modes.
 *              Shared UI between online (web) and offline (electron) frontends.
 */
export function NetworkSwitch({ isOffline, isLoading, onToggle, disabled = false }: NetworkSwitchProps) {
  const { t } = useTranslation();

  const handleClick = () => {
    if (disabled || isLoading) return;
    onToggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const label = isOffline
    ? t('network.offline', 'Offline')
    : t('network.online', 'Online');

  const title = isOffline
    ? t('network.switchToOnline', 'Cambiar a modo Online')
    : t('network.switchToOffline', 'Cambiar a modo Offline');

  return (
    <div
      className={`network-switch ${isOffline ? 'offline' : 'online'} ${isLoading ? 'loading' : ''} ${disabled ? 'disabled' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="switch"
      aria-checked={!isOffline}
      aria-label={title}
      title={title}
      tabIndex={0}
    >
      <span className="network-switch__label">{label}</span>
      <div className="network-switch__track">
        <div className="network-switch__thumb">
          {isLoading ? (
            <i className="las la-spinner la-spin" />
          ) : (
            <i className={`las ${isOffline ? 'la-cloud-download-alt' : 'la-wifi'}`} />
          )}
        </div>
      </div>
    </div>
  );
}
